/**
 * @file        src/components/BrandLogo.jsx
 * @description Beyond Destination brand logo. Renders the admin-uploaded
 *              storefront logo (brands.logo_url, tenant admin's Site Setting →
 *              Storefront Branding) when configured; otherwise the bundled
 *              raster asset at /public/logo.png; otherwise the full inline-SVG
 *              recreation (<BeyondLogo>, doodles + lockup) — the UI always
 *              shows a brand, never a broken image.
 *              `className` sizes both the <img> and the SVG (use an h-* height +
 *              w-auto). `colorClassName` sets the SVG lockup colour (currentColor)
 *              so it reads white on the dark nav / ink on light surfaces.
 * @author      Kinetika Labs
 * @created     2026-07-22
 * @changelog
 *   - 2026-10-23 PG — NAV_LOGO_SIZE moved here from TopNav and exported, so the
 *                     legal pages can show the logo at the home page's size.
 *   - 2026-10-15 PG — fetchPriority → fetchpriority. React 18 rejects the
 *                     camelCase prop ("does not recognize the fetchPriority
 *                     prop"); the lowercase attribute reaches the <img>.
 *   - 2026-08-10 Claude — Prefer the API-driven logoUrl (useCompanyInfo) over
 *                          the static /logo.png — previously Beyond ignored
 *                          the uploaded logo entirely regardless of admin
 *                          config (unlike Neon's Sidebar, the one place Neon
 *                          honours it). Falls through to /logo.png, then the
 *                          inline SVG, on any load failure.
 *   - 2026-08-04 Claude — ONE canonical size (LOGO_SIZE) for every surface, and
 *                          intrinsic width/height so the box is reserved before
 *                          the image decodes (no reflow flash on hard refresh).
 *   - 2026-07-22 Claude — Fallback upgraded from the plain wordmark to the full
 *                          BeyondLogo (doodles + lockup).
 *   - 2026-07-22 Claude — Initial.
 */
import React, { useState } from 'react';
import BeyondLogo from './BeyondLogo.jsx';
import BeyondWordmark from './BeyondWordmark.jsx';
import { useCompanyInfo } from '../hooks/useCompanyInfo.js';

/**
 * The ONE logo size, used by every surface (nav, sidebar, modals, voucher,
 * legal docs). Previously each caller picked its own height (h-9 / h-12 / h-14 /
 * h-16) or width (w-[132px]…w-[160px]) and the brand read inconsistently from
 * page to page. Change it here and it changes everywhere — do not re-introduce
 * per-call-site sizes.
 */
export const LOGO_SIZE = 'h-10 sm:h-14 w-auto';

// The home page nav's logo = 2× LOGO_SIZE (client request). The nav bar height
// (--nav-h) is sized to hold it with 6px of white above and below — change the
// two together. The legal pages use it too, so their logo matches the home page.
export const NAV_LOGO_SIZE = 'h-20 sm:h-28 w-auto';

// Intrinsic pixels of /public/logo.png. Passed as width/height so the browser
// reserves the correct box from the aspect ratio before the bytes arrive.
const NATURAL_W = 512;
const NATURAL_H = 321;

export default function BrandLogo({
  className = LOGO_SIZE,
  alt = 'Beyond Destination',
  colorClassName = 'text-white',
  showTagline = true,
  // full=true → the doodle logo (footer / splash); false → the slim lockup (nav).
  full = false,
}) {
  const { logoUrl } = useCompanyInfo();
  // Three-tier fallback: admin-uploaded logo -> bundled /logo.png -> inline SVG.
  // A failure on one tier advances to the next rather than jumping straight to
  // the SVG, so a bad upload doesn't hide the working bundled asset.
  const [tier, setTier] = useState('remote');
  const src = tier === 'remote' && logoUrl ? logoUrl : tier !== 'svg' ? '/logo.png' : null;

  if (!src) {
    return full
      ? <BeyondLogo className={className + ' ' + colorClassName} showTagline={showTagline} />
      : <BeyondWordmark className={className + ' ' + colorClassName} />;
  }
  return (
    <img
      key={src}
      src={src}
      alt={alt}
      className={className}
      width={NATURAL_W}
      height={NATURAL_H}
      // The logo is above the fold on every page — fetch it with the document,
      // not after React has mounted. index.html also preloads /logo.png.
      // Lowercase on purpose: React 18 does not know the camelCase prop and
      // warns; it passes an unknown lowercase attribute straight to the DOM.
      fetchpriority="high"
      decoding="async"
      draggable={false}
      onError={() => setTier((t) => (t === 'remote' ? 'bundled' : 'svg'))}
    />
  );
}
